import LoadMore from '~/src/common/components/LoadMore';
import ProductsGrid from '~/src/common/components/ProductsGrid';
import AdditionalProductsPanel from '~/src/common/components/Search/AdditionalProductsPanel/AdditionalProductsPanel';
import { getCanonicalIdFromId, sendCardEventClick } from '~/src/common/utils/product';

import { useSearchParams } from '../../hooks';
import { Results, TabName } from '../../types';
import Proposal from '../Proposal/Proposal';
import Empty from '../Status/Empty';
import Error from '../Status/Error';

import * as S from './layout';

interface Props {
  results: Results;
  currentTab: TabName;
  setCurrentTab: (tab: TabName) => void;
}

const Content = ({ results, currentTab, setCurrentTab }: Props) => {
  // Uniquement pour les analytics
  const { searchID } = useSearchParams();

  // Extraction des données
  const { isLoaded, term } = results;
  const data = results.data[currentTab];
  const { items, count, hasMore, isError, isFetching, loadMore } = data;

  // Onglet en cours
  const isProductTab = currentTab === 'products';
  const isRecipeTab = currentTab === 'recipes';

  // Conditions d'affichage des éléments
  const hasResults = !!items?.length;
  const hasNoResults = count === 0;
  const showErrorBox = isError;
  const showProposal = !isError && isProductTab;
  const showAdditionalProducts = isLoaded && !isError && isProductTab;

  // Bascule automatique vers les produits si il n'y aucune recette
  // Mettre à jour le state pendant le render permet d'améliorer les perfs et l'affichage
  // cf: https://react.dev/reference/react/useState#:~:text=When%20you%20call%20the%20set%20function%20during%20render
  if (isLoaded && isRecipeTab && hasNoResults) setCurrentTab('products');

  return (
    <S.Container>
      {hasResults && (
        <div>
          {/*
            Safari écrase les Grid placées dans des Flexbox verticale.
            Je n'ai rien trouvé de concluant en ligne concernant ce problème, et même
            surcharger le style de la grid via un `flex-shrink: 0;` ne fonctionne pas.
            Par contre, wrapper la Grid dans un div fonctionne, merci Safari ¯\_(ツ)_/¯
          */}
          <ProductsGrid
            products={items}
            eventSource="search results"
            onCardClick={item => sendCardEventClick(items, item, { 'search ID': searchID })}
          />
        </div>
      )}
      {showErrorBox && <Error term={term} isLoading={isFetching} onRetryClick={loadMore} />}
      {hasNoResults && <Empty term={term} />}
      {showProposal && <Proposal />}
      {showAdditionalProducts ? (
        <AdditionalProductsPanel
          text={term}
          excludeIds={items?.map(i => getCanonicalIdFromId(i.id))}
        />
      ) : null}
      <S.BottomSpace>
        <LoadMore loadMore={loadMore} isError={isError} isFetching={isFetching} hasMore={hasMore} />
      </S.BottomSpace>
    </S.Container>
  );
};

export default Content;
